import { Button } from "@/components/ui/button.tsx";
import { Calendar as MiniCalendar } from "@/components/ui/calendar.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { panelHeaderTitleClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { cn } from "@/lib/utils.ts";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

/**
 * The "Month Year" page title, with a chevron opening a popover with a mini
 * month calendar for jumping dates. Wears {@link panelHeaderTitleClass}, so the
 * calendar's title matches every other page title in the app.
 *
 * The popover is unconditional: it is the only way to jump to a date since the
 * mini calendars left with the calendar-nav sidebar.
 */
export const MonthTitlePicker = ({
  currentDate,
  onDateChange,
  className,
}: {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  // The mini-cal browses months on its own — paging it does NOT move the main
  // calendar; only picking a day does. Seed the browse month from the current
  // date each time the popover opens.
  const [displayMonth, setDisplayMonth] = useState(currentDate);
  const handleOpenChange = (next: boolean) => {
    if (next) setDisplayMonth(currentDate);
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          data-statsig-name="calendar-month-picker"
          className={cn(
            "h-auto min-w-0 gap-1.5 px-2 py-1",
            panelHeaderTitleClass,
            // -ml-2 cancels px-2 on the leading edge: the sidebar's title has no
            // horizontal padding, so without it the month title sits 8px further
            // in and the two columns' titles don't line up. The padding stays for
            // the ghost hover/pressed backdrop.
            "-ml-2",
            "[&[data-state=open]_.chevron]:rotate-180",
            className,
          )}
        >
          <span className="truncate">{format(currentDate, "MMM yyyy")}</span>
          <ChevronDown className="chevron size-4 shrink-0 text-muted-foreground transition-transform duration-200" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <MiniCalendar
          mode="single"
          fixedWeeks
          selected={currentDate}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          onSelect={(day) => {
            if (day) {
              // Follow the picked day's month (e.g. picking a trailing "outside"
              // day jumps the browse month to it) so the selection stays in view.
              setDisplayMonth(day);
              onDateChange(day);
              setOpen(false);
            }
          }}
          className="p-0"
          classNames={{
            // Drop the range-selection cell background — the default `cell` puts
            // `bg-accent` on the td behind a selected day, which reads as a grey
            // *square* under our grey *circle* button below.
            cell: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
            // Selected day: a grey circle (vs. the default filled-primary box).
            day_selected:
              "rounded-full! bg-accent text-accent-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            // Ring today in purple so it's identifiable. An outer `ring` (vs. a
            // border) stays visible even when today is also the selected day —
            // it haloes the filled cell rather than blending into it. `rounded-full!`
            // forces the circle over the day button's default `rounded-md`.
            day_today: "rounded-full! ring-2 ring-purple-500 font-semibold",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
